/**
 * Putting the page back after a guided flow — closing what the flow opened.
 *
 * 🔴 THIS IS "SKIP MODULE DOES NOT CLOSE THE CREATION PAGE". `TaskEngine.stop()`
 * removes the flow's OWN chrome (the ring, the callout, the bottom bar) and
 * nothing else, because it knows nothing about the host app. A flow that opened a
 * composer therefore handed the tour a page with a modal still on it — and a Radix
 * modal owns the whole screen: `pointer-events: none` on the body, `aria-hidden` on
 * everything outside itself, and a dimming backdrop over the lot. The next module's
 * highlight was drawn UNDER that backdrop, so the module was not shown properly and
 * the background never went back to normal.
 *
 * `TaskFlow.afterEnd` is the hook for undoing it (see its doc comment). This module
 * is the implementation THREE flows would otherwise each have written for
 * themselves — the campaigns/templates walkthrough, the social post flow and the
 * form builder flow all open one composer and all need it closed exactly the same
 * way. `automationFlow.ts` keeps its own `afterEnd` and is deliberately NOT routed
 * through here: it opens a ROUTE, not a dialog, and leaving it means answering an
 * unsaved-changes guard whose right answer depends on whether the record was ever
 * saved. That decision has no business being generalised.
 *
 * 🔴 SECOND CALLER: `TaskStep.rewind`, WHICH IS THE SAME BUG ONE BUTTON OVER.
 * Skip module leaving a composer up made the tour resume behind it; pressing BACK
 * over the step that opened it leaves the same composer covering the very button
 * that step is about, and `resolve()` will not ring a covered target — so the user
 * gets a step on screen with nothing highlighted. Both are "the flow opened
 * something and nothing closed it again", so both close it the same way. All four
 * registered flows now call this from both hooks; the `hook` argument only names
 * which one, for the warning.
 */

import { isVisible } from '../engine/anchors';
import { pressElement, waitUntil } from './formFill';

/** Anything that owns the screen the way a modal does. */
const DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]';

/**
 * The dialog currently on TOP, which is the only one that can be dismissed.
 *
 * 🔴 THE LAST ONE, NOT THE FIRST. A flow can be left with a picker open INSIDE its
 * composer — the campaign walkthrough's template picker is itself a `role="dialog"`
 * — and the composer is the earlier node. Aiming at the first would press a control
 * the picker is covering, which does nothing, and the loop below would spend its
 * whole budget getting nowhere.
 */
function topmostDialog(): HTMLElement | null {
  const open = Array.from(document.querySelectorAll<HTMLElement>(DIALOG_SELECTOR)).filter(isVisible);
  return open.length > 0 ? open[open.length - 1] : null;
}

/**
 * A dialog's own close control.
 *
 * `aria-label="Close"` is what every composer in this app labels it — verified in a
 * real browser on all four (campaign, template, social post, form). Matched on the
 * LABEL rather than on an icon, a class or a position, because those are the three
 * things a redesign changes.
 */
function closeControlIn(dialog: HTMLElement): HTMLElement | null {
  return (
    Array.from(dialog.querySelectorAll<HTMLElement>('button')).find(
      (button) => button.getAttribute('aria-label')?.trim().toLowerCase() === 'close' && isVisible(button),
    ) ?? null
  );
}

/**
 * Escape, dispatched at the document the way the key actually arrives.
 *
 * The fallback for a layer with no close control of its own — a popover, a select,
 * a picker. Radix listens for `keydown` on the document, so this is the real path
 * and not a simulation of one.
 */
function pressEscape(): void {
  const event = new KeyboardEvent('keydown', {
    key: 'Escape',
    code: 'Escape',
    keyCode: 27,
    which: 27,
    bubbles: true,
    cancelable: true,
  } as KeyboardEventInit);
  document.dispatchEvent(event);
}

/**
 * How many layers deep this will dig before giving up.
 *
 * 🔴 A LOOP, NOT ONE PRESS, AND THAT IS NOT DEFENSIVE PADDING. Measured in a real
 * browser: with the campaign composer's template picker open, the first Escape
 * closes the PICKER and leaves the composer exactly where it was. A single attempt
 * therefore reports success while the modal it was asked to close is still on
 * screen — the original bug, one layer down.
 */
const MAX_LAYERS = 4;

/** Per-attempt wait. Four of these stay well inside `AFTER_END_TIMEOUT_MS`. */
const SETTLE_MS = 600;

/**
 * Close whatever the flow left open, and say whether the screen came back.
 *
 * `isOpen` is the FLOW'S OWN predicate for "my composer is still up" — the same one
 * its steps already use — rather than "is any dialog present". Two reasons: a
 * nested picker is a dialog and closing it is not the job being asked for, and a
 * dialog this flow never opened is not this flow's to close.
 *
 * 🔴 IDEMPOTENT, AND IT HAS TO BE. `afterEnd` runs on EVERY exit, including a
 * FINISHED run that already submitted and closed its own composer — that call must
 * find nothing to do and cost nothing, not press Escape at a page that has moved on.
 *
 * 🔴 NEVER DESTROYS ANYTHING THE USER OWNS. Every composer this is used for creates
 * nothing until it is submitted, so closing one discards only unsaved draft input —
 * and on these flows that input was typed by the guide itself. Verified in a real
 * browser on all four composers: closing asks for no confirmation, with or without
 * content in the form, and leaves the app on the module's own list page. A flow
 * whose exit CAN destroy a real record must not use this (see the note above about
 * `automationFlow.ts`).
 */
export async function closeFlowChrome(
  isOpen: () => boolean,
  /**
   * Which hook is asking, for the warning below only.
   *
   * Two callers now: `afterEnd` (the flow is ending) and `TaskStep.rewind` (the
   * user pressed Back over the step that opened this). The console line is the
   * one signal that a stuck composer is a stuck COMPOSER rather than a broken
   * tour, so it has to name the right half — "afterEnd" on a Back press sends
   * whoever reads it looking at exit code that never ran.
   */
  hook: 'afterEnd' | 'rewind' = 'afterEnd',
): Promise<boolean> {
  for (let attempt = 0; attempt < MAX_LAYERS && isOpen(); attempt += 1) {
    const dialog = topmostDialog();
    const closer = dialog ? closeControlIn(dialog) : null;

    if (closer) pressElement(closer);
    else pressEscape();

    // Waits on the FLOW's predicate, so peeling a nested layer simply costs one
    // attempt rather than counting as done.
    await waitUntil(() => !isOpen(), SETTLE_MS);
  }

  if (isOpen()) {
    // Always-on, not DEV-gated: this is the difference between "the tour resumed
    // normally" and "the tour resumed under a backdrop", and the latter is
    // otherwise reported as the tour being broken rather than as a stuck dialog.
    console.warn(
      `[tour-guide] ${hook}: a composer opened by the flow is still on screen after ` +
        `${MAX_LAYERS} attempts to close it. ` +
        (hook === 'afterEnd'
          ? 'The tour will resume behind it.'
          : 'The step Back is landing on will have nothing to highlight.'),
    );
    return false;
  }
  return true;
}
